import { type GameData, WEEK, DAY, capitalize, pluralize } from '@terros/common'
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react'

export const DURATION_TYPES = ['week', 'day', 'custom'] as const
export type GameDurationType = (typeof DURATION_TYPES)[number]

export type GameDuration = {
  duration: number
  durationType: GameDurationType
}

type SetEndDateFn = (endDate: number) => void

type Props = {
  game: GameData
  setEndDate: SetEndDateFn
  onDurationChange?: () => void
}
export const useGameDurationOptions = ({ game, setEndDate, onDurationChange }: Props) => {
  const [durationOption, _setDurationOption] = useState<GameDuration>(getGameDurationInitialValues(game))
  const setDurationOption: Dispatch<SetStateAction<GameDuration>> = useCallback(
    (input) => {
      onDurationChange?.()

      _setDurationOption((prev) => {
        const newValue = typeof input === 'function' ? input(prev) : input
        if (newValue.durationType !== 'custom') {
          const newEndDate = getNewGameEndDate(game.startDate, newValue.duration, newValue.durationType)
          setEndDate(newEndDate)
        }
        return newValue
      })
    },
    [durationOption, game.startDate, setEndDate, onDurationChange]
  )

  const durationTypeOptions = useMemo(
    () =>
      DURATION_TYPES.map((type) => {
        const label = stringifyDurationType(durationOption.duration, type)
        return { label, value: type }
      }),
    [durationOption.duration]
  )

  const durationOptions = useMemo(() => {
    return Array.from({ length: 6 }, (_, index) => {
      const num = (index + 1).toString()
      return { label: num, value: num }
    })
  }, [])

  const isCustom = useMemo(() => durationOption.durationType === 'custom', [durationOption.durationType])

  return useMemo(
    () => ({ durationTypeOptions, durationOptions, durationOption, setDurationOption, isCustom }),
    [durationTypeOptions, durationOptions, durationOption, setDurationOption, isCustom]
  )
}

export const getNewGameEndDate = (
  startDate: number,
  duration: number,
  durationType: Exclude<GameDurationType, 'custom'>
) => {
  return startDate + duration * getDurationTypeMultiplier(durationType)
}

const getGameDurationInitialValues = (game: GameData): GameDuration => {
  const duration = game.endDate - game.startDate

  if (duration < WEEK) {
    const days = duration / DAY
    if (days % 1 === 0) {
      return {
        duration: days,
        durationType: 'day',
      }
    }
    return {
      duration: Math.floor(days),
      durationType: 'custom',
    }
  }

  const weeks = duration / WEEK
  if (weeks < 6 && weeks % 1 === 0) {
    return {
      duration: weeks,
      durationType: 'week',
    }
  }

  return {
    duration: Math.floor(weeks),
    durationType: 'custom',
  }
}

const getDurationTypeMultiplier = (durationType: Exclude<GameDurationType, 'custom'>) => {
  if (durationType === 'day') {
    return DAY
  }
  return WEEK
}

const stringifyDurationType = (duration: number, durationType: GameDurationType) => {
  if (durationType === 'custom') {
    return 'Custom'
  }
  return capitalize(pluralize(duration, durationType))
}
